import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { taskService } from '../../services/task.service';
import { userService } from '../../services/user.service';
import { FaPlus, FaEdit, FaTrash, FaEye, FaFilter, FaSort, FaSearch } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const AdminTasks = () => {
    const navigate = useNavigate();
    const [tasks, setTasks] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({});

    // Filters
    const [statusFilter, setStatusFilter] = useState('all');
    const [priorityFilter, setPriorityFilter] = useState('all');
    const [assignedToFilter, setAssignedToFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    // Sorting
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('desc');

    // Modals
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        assignedTo: '',
        priority: 'medium',
        dueDate: '',
        tags: ''
    });

    useEffect(() => {
        fetchTasks();
        fetchUsers();
        fetchStats();
    }, [statusFilter, priorityFilter, assignedToFilter, sortBy, sortOrder]);

    const fetchTasks = async () => {
        try {
            setLoading(true);
            const params = {};

            if (statusFilter !== 'all') params.status = statusFilter;
            if (priorityFilter !== 'all') params.priority = priorityFilter;
            if (assignedToFilter !== 'all') params.assignedTo = assignedToFilter;

            const data = await taskService.getAllTasks(params);
            let tasksList = data.tasks || [];

            // Apply search
            if (searchTerm) {
                tasksList = tasksList.filter(task =>
                    task.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    task.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    task.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
                );
            }

            // Apply sorting
            tasksList = sortTasks(tasksList);

            setTasks(tasksList);
        } catch (error) {
            toast.error('Failed to fetch tasks');
        } finally {
            setLoading(false);
        }
    };

    const sortTasks = (tasksList) => {
        return [...tasksList].sort((a, b) => {
            let aValue, bValue;

            switch (sortBy) {
                case 'title':
                    aValue = a.title?.toLowerCase() || '';
                    bValue = b.title?.toLowerCase() || '';
                    break;
                case 'priority':
                    const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
                    aValue = priorityOrder[a.priority] || 0;
                    bValue = priorityOrder[b.priority] || 0;
                    break;
                case 'dueDate':
                    aValue = new Date(a.dueDate);
                    bValue = new Date(b.dueDate);
                    break;
                case 'status':
                    aValue = a.status || '';
                    bValue = b.status || '';
                    break;
                case 'createdAt':
                default:
                    aValue = new Date(a.createdAt);
                    bValue = new Date(b.createdAt);
            }

            if (sortOrder === 'asc') {
                return aValue > bValue ? 1 : -1;
            } else {
                return aValue < bValue ? 1 : -1;
            }
        });
    };

    const fetchUsers = async () => {
        try {
            const data = await userService.getAllUsers({ status: 'active' });
            setUsers(data.users || []);
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    const fetchStats = async () => {
        try {
            const data = await taskService.getTaskStats();
            console.log('Stats received:', data); // Debug log

            // The backend returns: { success: true, stats: { total, pending, inProgress, completed, ... } }
            if (data && data.stats) {
                setStats(data.stats);
            }
        } catch (error) {
            console.error('Failed to fetch stats:', error);
            // Set empty stats on error
            setStats({
                total: 0,
                pending: 0,
                inProgress: 0,
                completed: 0,
                cancelled: 0,
                dueToday: 0
            });
        }
    };

    const handleCreateTask = async (e) => {
        e.preventDefault();

        try {
            const taskData = {
                ...formData,
                tags: formData.tags.split(',').map(t => t.trim()).filter(t => t)
            };

            await taskService.createTask(taskData);
            toast.success('Task created successfully');
            setShowCreateModal(false);
            resetForm();
            fetchTasks();
            fetchStats();
        } catch (error) {
            toast.error('Failed to create task');
        }
    };

    const handleDeleteTask = async (taskId) => {
        if (!window.confirm('Are you sure you want to delete this task?')) return;

        try {
            await taskService.deleteTask(taskId);
            toast.success('Task deleted');
            fetchTasks();
            fetchStats();
        } catch (error) {
            toast.error('Failed to delete task');
        }
    };

    const handleStatusChange = async (taskId, newStatus) => {
        try {
            await taskService.updateTaskStatus(taskId, newStatus);
            toast.success('Status updated');
            fetchTasks();
            fetchStats();
        } catch (error) {
            toast.error('Failed to update status');
        }
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            assignedTo: '',
            priority: 'medium',
            dueDate: '',
            tags: ''
        });
        setSelectedTask(null);
    };

    const clearFilters = () => {
        setStatusFilter('all');
        setPriorityFilter('all');
        setAssignedToFilter('all');
        setSearchTerm('');
        setSortBy('createdAt');
        setSortOrder('desc');
    };

    const getStatusBadge = (status) => {
        const badges = {
            pending: 'bg-gray-100 text-gray-800',
            'in-progress': 'bg-blue-100 text-blue-800',
            completed: 'bg-green-100 text-green-800',
            cancelled: 'bg-red-100 text-red-800'
        };
        return badges[status] || 'bg-gray-100 text-gray-800';
    };

    const getPriorityBadge = (priority) => {
        const badges = {
            low: 'bg-gray-100 text-gray-600',
            medium: 'bg-yellow-100 text-yellow-800',
            high: 'bg-orange-100 text-orange-800',
            urgent: 'bg-red-100 text-red-800'
        };
        return badges[priority] || 'bg-gray-100 text-gray-800';
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString();
    };

    const isOverdue = (dueDate, status) => {
        return status !== 'completed' && new Date(dueDate) < new Date();
    };

    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-3xl font-bold">Manage Tasks</h1>
                    <p className="text-gray-600 mt-2">Create and manage all tasks</p>
                </div>
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                    <FaPlus /> Create Task
                </button>
            </div>

            {/* Stats Cards - FIXED VERSION */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-6">
                <div className="bg-white p-6 rounded-lg shadow">
                    <p className="text-gray-500 text-sm">Total Tasks</p>
                    <p className="text-3xl font-bold text-blue-600 mt-2">
                        {stats.total || 0}
                    </p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow">
                    <p className="text-gray-500 text-sm">Pending</p>
                    <p className="text-3xl font-bold text-gray-600 mt-2">
                        {stats.pending || 0}
                    </p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow">
                    <p className="text-gray-500 text-sm">In Progress</p>
                    <p className="text-3xl font-bold text-blue-600 mt-2">
                        {stats.inProgress || 0}
                    </p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow">
                    <p className="text-gray-500 text-sm">Completed</p>
                    <p className="text-3xl font-bold text-green-600 mt-2">
                        {stats.completed || 0}
                    </p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow">
                    <p className="text-gray-500 text-sm">Due Today</p>
                    <p className="text-3xl font-bold text-orange-600 mt-2">
                        {stats.dueToday || 0}
                    </p>
                </div>
            </div>

            {/* Search, Filters & Sort */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    {/* Search */}
                    <div className="lg:col-span-2">
                        <label className="block text-sm font-medium mb-2">
                            <FaSearch className="inline mr-2" />
                            Search Tasks
                        </label>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search by title, description, or tags..."
                            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    {/* Sort By */}
                    <div>
                        <label className="block text-sm font-medium mb-2">
                            <FaSort className="inline mr-2" />
                            Sort By
                        </label>
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="createdAt">Created Date</option>
                            <option value="dueDate">Due Date</option>
                            <option value="priority">Priority</option>
                            <option value="status">Status</option>
                            <option value="title">Title</option>
                        </select>
                    </div>

                    {/* Sort Order */}
                    <div>
                        <label className="block text-sm font-medium mb-2">Order</label>
                        <select
                            value={sortOrder}
                            onChange={(e) => setSortOrder(e.target.value)}
                            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="desc">Descending</option>
                            <option value="asc">Ascending</option>
                        </select>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {/* Status Filter */}
                    <div>
                        <label className="block text-sm font-medium mb-2">
                            <FaFilter className="inline mr-2" />
                            Status
                        </label>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="all">All Statuses</option>
                            <option value="pending">Pending</option>
                            <option value="in-progress">In Progress</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                    </div>

                    {/* Priority Filter */}
                    <div>
                        <label className="block text-sm font-medium mb-2">Priority</label>
                        <select
                            value={priorityFilter}
                            onChange={(e) => setPriorityFilter(e.target.value)}
                            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="all">All Priorities</option>
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                            <option value="urgent">Urgent</option>
                        </select>
                    </div>

                    {/* Assigned To Filter */}
                    <div>
                        <label className="block text-sm font-medium mb-2">Assigned To</label>
                        <select
                            value={assignedToFilter}
                            onChange={(e) => setAssignedToFilter(e.target.value)}
                            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="all">All Users</option>
                            {users.map(user => (
                                <option key={user._id} value={user._id}>{user.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Clear Filters */}
                    <div className="flex items-end">
                        <button
                            onClick={clearFilters}
                            className="w-full px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                        >
                            Clear All
                        </button>
                    </div>
                </div>
            </div>

            {/* Results Info */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
                <p className="text-gray-600">
                    Showing {tasks.length} task{tasks.length !== 1 ? 's' : ''}
                    {statusFilter !== 'all' && ` • Status: ${statusFilter}`}
                    {priorityFilter !== 'all' && ` • Priority: ${priorityFilter}`}
                    {searchTerm && ` • Search: "${searchTerm}"`}
                </p>
            </div>

            {/* Tasks Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    </div>
                ) : tasks.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                        No tasks found
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned To</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {tasks.map((task) => (
                                    <tr key={task._id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4">
                                            <div>
                                                <p className="font-medium">{task.title}</p>
                                                <p className="text-sm text-gray-500 line-clamp-1">{task.description}</p>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm">
                                            {task.assignedTo?.name || 'Unassigned'}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-1 text-xs rounded capitalize ${getPriorityBadge(task.priority)}`}>
                                                {task.priority}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <select
                                                value={task.status}
                                                onChange={(e) => handleStatusChange(task._id, e.target.value)}
                                                className={`px-2 py-1 text-xs rounded capitalize ${getStatusBadge(task.status)}`}
                                            >
                                                <option value="pending">Pending</option>
                                                <option value="in-progress">In Progress</option>
                                                <option value="completed">Completed</option>
                                                <option value="cancelled">Cancelled</option>
                                            </select>
                                        </td>
                                        <td className="px-6 py-4 text-sm">
                                            <span className={isOverdue(task.dueDate, task.status) ? 'text-red-600 font-medium' : ''}>
                                                {formatDate(task.dueDate)}
                                                {isOverdue(task.dueDate, task.status) && ' ⚠️'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex gap-2">
                                                {/*<button*/}
                                                {/*    onClick={() => navigate(`/dashboard/tasks/${task._id}`)}*/}
                                                {/*    className="p-2 text-blue-600 hover:bg-blue-50 rounded"*/}
                                                {/*    title="View"*/}
                                                {/*>*/}
                                                {/*    <FaEye />*/}
                                                {/*</button>*/}
                                                {/*<button*/}
                                                {/*    onClick={() => navigate(`/dashboard/tasks/edit/${task._id}`)}*/}
                                                {/*    className="p-2 text-green-600 hover:bg-green-50 rounded"*/}
                                                {/*    title="Edit"*/}
                                                {/*>*/}
                                                {/*    <FaEdit />*/}
                                                {/*</button>*/}
                                                <button
                                                    onClick={() => handleDeleteTask(task._id)}
                                                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                                                    title="Delete"
                                                >
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Create Task Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <h2 className="text-2xl font-bold mb-6">Create New Task</h2>

                        <form onSubmit={handleCreateTask} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-2">Title *</label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2">Description *</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                    rows="4"
                                    required
                                ></textarea>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-2">Assign To *</label>
                                    <select
                                        value={formData.assignedTo}
                                        onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })}
                                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                        required
                                    >
                                        <option value="">Select User</option>
                                        {users.map(user => (
                                            <option key={user._id} value={user._id}>{user.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">Priority *</label>
                                    <select
                                        value={formData.priority}
                                        onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="low">Low</option>
                                        <option value="medium">Medium</option>
                                        <option value="high">High</option>
                                        <option value="urgent">Urgent</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2">Due Date *</label>
                                <input
                                    type="date"
                                    value={formData.dueDate}
                                    onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2">Tags (comma separated)</label>
                                <input
                                    type="text"
                                    value={formData.tags}
                                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="backend, api, urgent"
                                />
                            </div>

                            <div className="flex gap-4 pt-4">
                                <button
                                    type="submit"
                                    className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
                                >
                                    Create Task
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowCreateModal(false);
                                        resetForm();
                                    }}
                                    className="flex-1 bg-gray-300 py-2 rounded-lg hover:bg-gray-400"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminTasks;